import React from 'react';
import { useSelector } from 'react-redux';
import { RatingCardLegacy } from './RatingCard.legacy';
import Styles from './RatingDetails.legacy.module.scss';

/**
 * The pre-GOTU-7339 Rating tab, restored from `b83c6bd9` for the GOTU-6266
 * flag-off path. `RatingDetails` renders this when the flag is off.
 *
 * **Both cards come from `state.jobs.rating`** — the monolith's
 * `admin/jobs/{id}` payload, already in Redux before the tab mounts. That is
 * the whole difference from `RatingDetailsV2`, which reads the office side from
 * organization-service by shift id: nothing here touches the network, so the
 * tab works with the service undeployed.
 *
 * The two directions are easy to cross, so to be explicit: `toDentist` is the
 * professional's review *of the office* (titled `DENTIST`, named by
 * `office_name`), and `toHygienist` is the office's review *of the
 * professional*. GOTU-7339 keeps only the second on Redux.
 *
 * Restored as-was apart from the inline styles moving to a scss module. The
 * quirks are deliberate, because production has them today:
 *
 * - The office side's only row is `Rating`, fed from `officeRating.averageRating`
 *   — a field the monolith never populates for a professional→office review, so
 *   it renders empty. That dead row is the reason GOTU-7339 split the card.
 * - `Confidential Feedback for GoTu` shows on both cards and reads `N/A` on the
 *   office one, for the same reason.
 * - A falsy `averageRating` renders no star at all, so a genuine 0 is
 *   indistinguishable from missing data.
 */
const RatingDetailsLegacy = () => {
  const rating = useSelector((state) => state.jobs.rating);
  const hygienistRating = rating?.toDentist;
  const dentistRating = rating?.toHygienist;

  const hygienistRatingItems = [
    { label: 'Rating', value: hygienistRating?.officeRating?.averageRating },
  ];

  const dentistRatingItems = dentistRating?.professionalRating
    ? [
      {
        label: 'Professional Skills',
        value: dentistRating?.professionalRating?.professionalSkills,
      },
      {
        label: 'Interpersonal Skills',
        value: dentistRating?.professionalRating?.interpersonalSkills,
      },
      { label: 'Time Management', value: dentistRating?.professionalRating?.timeManagement },
    ]
    : [{ label: 'Rating', value: dentistRating?.rating_count }];

  return (
    <div className={Styles.cards}>
      {hygienistRating ? (
        <RatingCardLegacy
          title="DENTIST"
          name={hygienistRating?.rating_to?.office_name}
          averageRating={hygienistRating?.rating_count}
          ratingItems={hygienistRatingItems}
          feedback={hygienistRating?.feedback_for_tempmee}
        />
      ) : (
        <div className={Styles.placeholder} />
      )}
      {dentistRating ? (
        <RatingCardLegacy
          title="DENTAL PROFESSIONAL"
          name={dentistRating?.rating_to?.full_name}
          averageRating={dentistRating?.professionalRating?.averageRating}
          ratingItems={dentistRatingItems}
          feedback={dentistRating?.feedback_for_tempmee}
        />
      ) : (
        <div className={Styles.placeholder} />
      )}
    </div>
  );
};

export { RatingDetailsLegacy };
export default RatingDetailsLegacy;
